import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomSnackbar from "../components/CustomSnackbar";
import JigsawDieLineOverlay from "../../pages/puzzles/jigsaw/JigsawDieLineOverlay";
import {
    JIGSAW_DIFFICULTY_LABELS,
    resolveJigsawTemplateTileSize,
} from "../../pages/puzzles/jigsaw/jigsawBrowseUtils";
import AdminJigsawService from "./AdminJigsawService";
import { isJigsawTemplateEnabled } from "./AdminJigsawTemplateService";
import AdminColorBasketMiniMap from "./play/AdminColorBasketMiniMap";
import AdminColorBasketPlayPanel from "./play/AdminColorBasketPlayPanel";
import AdminColorBasketTrays from "./play/AdminColorBasketTrays";
import AdminJigsawPlayService from "./play/AdminJigsawPlayService";

const DEFAULT_BASKET_COUNTS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const DIFF_LABEL = { ...JIGSAW_DIFFICULTY_LABELS };

/** Prefer lowest allowed K (playable / fewer trays), not max. */
function preferAllowedBasketCount(allowed, meta) {
    const list = (Array.isArray(allowed) ? allowed : [])
        .map(Number)
        .filter((n) => Number.isFinite(n) && n >= 2);
    if (list.length === 0) return 3;
    const freeMax = Number(meta?.freeMaxCount);
    if (Number.isFinite(freeMax) && freeMax >= 2) {
        if (list.includes(freeMax)) return freeMax;
        const freeAllowed = list.filter((n) => n <= freeMax);
        if (freeAllowed.length > 0) return freeAllowed[freeAllowed.length - 1];
    }
    return Math.min(...list);
}

function getStoryTitle(story) {
    if (!story) return "";
    const en = story.english?.title ?? story.en?.title ?? "";
    const fr = story.french?.title ?? story.fr?.title ?? "";
    return en || fr || "";
}

function getImageUrl(story) {
    if (!story) return null;
    return story.imageUrl ?? story.imagePreviewUrl ?? story.imageIcon400Url ?? null;
}

function normalizePiecePaths(tpl) {
    const raw = tpl?.piecePaths?.pieces ?? tpl?.piecePaths;
    if (!Array.isArray(raw)) return null;
    return raw.map((piece) => ({
        ...piece,
        path: piece?.path ?? piece?.d ?? null,
    }));
}

export default function AdminJigsawColorBasketDetailPage() {
    const { storyId, jigsawId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null);
    const [error, setError] = useState(null);
    const [basketCount, setBasketCount] = useState(null);
    const [allowedBasketCounts, setAllowedBasketCounts] = useState(DEFAULT_BASKET_COUNTS);
    const [colorBasketMeta, setColorBasketMeta] = useState(null);
    const [colorBasketResult, setColorBasketResult] = useState(null);
    const [basketBusy, setBasketBusy] = useState(false);
    const [showColorBaskets, setShowColorBaskets] = useState(false);
    const [boardBasketBusy, setBoardBasketBusy] = useState(false);
    const [selectedBasketIndex, setSelectedBasketIndex] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const fetchGroup = useCallback(async () => {
        if (!storyId) return;
        setLoading(true);
        setError(null);
        const result = await AdminJigsawService.getGroupByStoryId(storyId);
        if (result.success) {
            setGroup(result.data);
        } else {
            setError(result.error || "Failed to load");
            setGroup(null);
        }
        setLoading(false);
    }, [storyId]);

    useEffect(() => {
        fetchGroup();
    }, [fetchGroup]);

    const story = group?.story ?? null;
    const storyJigsaw = group?.storyJigsaw ?? null;
    const title = getStoryTitle(story) || storyId || "—";
    const imageUrl = getImageUrl(story);
    const templates = group?.templates ?? [];

    const selectedTpl = useMemo(() => {
        if (!jigsawId || !Array.isArray(templates)) return null;
        return templates.find((tpl) => String(tpl?.id) === String(jigsawId)) ?? null;
    }, [templates, jigsawId]);

    const paths = useMemo(() => normalizePiecePaths(selectedTpl), [selectedTpl]);
    const grid = resolveJigsawTemplateTileSize(selectedTpl) || 0;
    const svgPathFallback = selectedTpl?.svgPath ?? null;
    const isDisabledTemplate = selectedTpl ? !isJigsawTemplateEnabled(selectedTpl) : false;
    const colorBasketCount = Number(selectedTpl?.colorBasketCount);
    const templateCount = templates.length;

    useEffect(() => {
        let cancelled = false;
        setColorBasketResult(null);
        setColorBasketMeta(null);
        setShowColorBaskets(false);
        setSelectedBasketIndex(null);
        if (!jigsawId) {
            setAllowedBasketCounts(DEFAULT_BASKET_COUNTS);
            return undefined;
        }

        (async () => {
            try {
                const meta = await AdminJigsawPlayService.getColorBaskets(jigsawId);
                if (cancelled) return;
                setColorBasketMeta(meta);
                const allowed =
                    Array.isArray(meta?.allowedCounts) && meta.allowedCounts.length > 0
                        ? meta.allowedCounts
                        : DEFAULT_BASKET_COUNTS;
                setAllowedBasketCounts(allowed);
                const preferred = preferAllowedBasketCount(allowed, meta);
                setBasketCount(allowed.includes(preferred) ? preferred : Number(allowed[0]));
            } catch {
                if (cancelled) return;
                setAllowedBasketCounts(DEFAULT_BASKET_COUNTS);
                setBasketCount(preferAllowedBasketCount(DEFAULT_BASKET_COUNTS));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [jigsawId]);

    const loadBasketsForCount = useCallback(
        async (k, { showOnSuccess = true } = {}) => {
            if (!jigsawId) return null;
            const result = await AdminJigsawPlayService.getColorBaskets(jigsawId, k);
            setColorBasketResult(result);
            if (result?.available === false) {
                setShowColorBaskets(false);
                return result;
            }
            const n = Array.isArray(result?.baskets) ? result.baskets.length : 0;
            if (showOnSuccess && n > 0) {
                setShowColorBaskets(true);
            } else if (n === 0) {
                setShowColorBaskets(false);
            }
            return result;
        },
        [jigsawId],
    );

    useEffect(() => {
        let cancelled = false;
        if (!jigsawId || basketCount == null || !allowedBasketCounts.includes(basketCount)) {
            return undefined;
        }

        (async () => {
            setBoardBasketBusy(true);
            try {
                const result = await AdminJigsawPlayService.getColorBaskets(jigsawId, basketCount);
                if (cancelled) return;
                setColorBasketResult(result);
                // Keep heavy piece trays off until "Show baskets" — K can be large (e.g. 36).
                setShowColorBaskets(false);
            } catch {
                if (cancelled) return;
                setColorBasketResult(null);
                setShowColorBaskets(false);
            } finally {
                if (!cancelled) setBoardBasketBusy(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [jigsawId, basketCount, allowedBasketCounts]);

    const handleGenerateColorBaskets = async () => {
        if (!jigsawId) {
            setSnackbar({ open: true, severity: "warning", message: "Missing jigsaw id." });
            return;
        }
        setBasketBusy(true);
        try {
            const result = await AdminJigsawPlayService.recomputeColorBaskets(jigsawId, basketCount);
            setColorBasketResult(result);
            if (result?.available === false) {
                setShowColorBaskets(false);
                setSnackbar({
                    open: true,
                    severity: "warning",
                    message: `Color baskets unavailable: ${result.reason || "unknown reason"}`,
                });
            } else {
                const n = Array.isArray(result?.baskets) ? result.baskets.length : 0;
                setShowColorBaskets(n > 0);
                setSnackbar({
                    open: true,
                    severity: "success",
                    message: `Generated ${n} color basket${n === 1 ? "" : "s"} (K=${result?.basketCount ?? basketCount}).`,
                });
                fetchGroup();
            }
        } catch (e) {
            setSnackbar({
                open: true,
                severity: "error",
                message: e?.message || "Failed to generate color baskets.",
            });
        } finally {
            setBasketBusy(false);
        }
    };

    const handleShowColorBaskets = async () => {
        if (!jigsawId) {
            setSnackbar({ open: true, severity: "warning", message: "Missing jigsaw id." });
            return;
        }
        if (showColorBaskets) {
            setShowColorBaskets(false);
            return;
        }

        const existing =
            colorBasketResult?.available !== false &&
            Number(colorBasketResult?.basketCount) === Number(basketCount) &&
            Array.isArray(colorBasketResult?.baskets) &&
            colorBasketResult.baskets.length > 0
                ? colorBasketResult
                : null;

        if (existing) {
            setShowColorBaskets(true);
            return;
        }

        setBoardBasketBusy(true);
        try {
            const result = await loadBasketsForCount(basketCount);
            if (result?.available === false) {
                setSnackbar({
                    open: true,
                    severity: "warning",
                    message: `Color baskets unavailable: ${result.reason || "unknown reason"}`,
                });
                return;
            }
            const n = Array.isArray(result?.baskets) ? result.baskets.length : 0;
            if (n === 0) {
                setSnackbar({
                    open: true,
                    severity: "warning",
                    message: "No baskets returned. Try Generate (recompute) first.",
                });
            }
        } catch (e) {
            setSnackbar({
                open: true,
                severity: "error",
                message: e?.message || "Failed to load color baskets.",
            });
        } finally {
            setBoardBasketBusy(false);
        }
    };

    const baskets = Array.isArray(colorBasketResult?.baskets) ? colorBasketResult.baskets : [];
    const selectedBasket = useMemo(() => {
        if (selectedBasketIndex == null) return null;
        return baskets.find((b) => Number(b?.index) === Number(selectedBasketIndex)) ?? null;
    }, [baskets, selectedBasketIndex]);

    const handleSelectBasket = useCallback((index) => {
        setSelectedBasketIndex(index);
    }, []);

    useEffect(() => {
        if (selectedBasketIndex == null) return undefined;
        const t = window.setTimeout(() => {
            document.getElementById("admin-color-basket-practice")?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }, 50);
        return () => window.clearTimeout(t);
    }, [selectedBasketIndex]);

    const backToStory = () =>
        navigate(`/admin/jigsaw/browse/${encodeURIComponent(storyId)}`, { state: location.state });

    return (
        <Box sx={{ p: 3, maxWidth: selectedBasket ? 1600 : 1200, mx: "auto", width: "100%" }}>
            <Button startIcon={<ArrowBackIcon />} onClick={backToStory} sx={{ mb: 2 }}>
                Back to story
            </Button>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : !selectedTpl ? (
                <Typography color="error">Jigsaw template not found for this story.</Typography>
            ) : (
                <Stack spacing={3}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, minWidth: 0 }}>
                            {title}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                            {storyJigsaw?.state && (
                                <Chip
                                    size="small"
                                    label={storyJigsaw.state}
                                    color={storyJigsaw.state === "PUBLISHED" ? "success" : "default"}
                                />
                            )}
                            {storyJigsaw && (
                                <Chip
                                    size="small"
                                    label={storyJigsaw.free ? "Free" : "Paid"}
                                    color={storyJigsaw.free ? "success" : "default"}
                                    variant="outlined"
                                />
                            )}
                            <Chip
                                size="small"
                                label={`${templateCount} template${templateCount !== 1 ? "s" : ""}`}
                                variant="outlined"
                            />
                            <Chip
                                size="small"
                                label={`Color baskets: ${
                                    Number.isFinite(colorBasketCount) && colorBasketCount > 0
                                        ? colorBasketCount
                                        : 0
                                }`}
                                color={
                                    Number.isFinite(colorBasketCount) && colorBasketCount > 0
                                        ? "success"
                                        : "default"
                                }
                                variant="outlined"
                            />
                        </Stack>
                    </Paper>

                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            ...(isDisabledTemplate ? { bgcolor: "action.hover", opacity: 0.88 } : undefined),
                        }}
                    >
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 600,
                                        color: isDisabledTemplate ? "text.disabled" : "text.primary",
                                        fontStyle: isDisabledTemplate ? "italic" : "normal",
                                    }}
                                >
                                    {selectedTpl.templateName || selectedTpl.name || "Selected template"}
                                </Typography>
                                {isDisabledTemplate ? (
                                    <Chip size="small" label="Disabled" variant="outlined" />
                                ) : null}
                                {selectedTpl.tileSize ? (
                                    <Chip size="small" label={`${selectedTpl.tileSize}×${selectedTpl.tileSize}`} />
                                ) : null}
                                {selectedTpl.difficultyLevel ? (
                                    <Chip
                                        size="small"
                                        label={
                                            DIFF_LABEL[String(selectedTpl.difficultyLevel).toUpperCase()] ??
                                            selectedTpl.difficultyLevel
                                        }
                                        variant="outlined"
                                    />
                                ) : null}
                            </Stack>

                            {imageUrl ? (
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        maxWidth: 520,
                                        aspectRatio: "1 / 1",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        border: 1,
                                        borderColor: "divider",
                                        bgcolor: "grey.900",
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={imageUrl}
                                        alt={title}
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                        }}
                                    />
                                    <JigsawDieLineOverlay
                                        pieces={selectedTpl.piecePaths?.pieces ?? null}
                                        svgPathFallback={svgPathFallback}
                                        bevel3d
                                        opacity={0.95}
                                        strokeWidth={2.5}
                                        preserveAspectRatio="xMidYMid slice"
                                        zIndex={1}
                                    />
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No story image available for this puzzle.
                                </Typography>
                            )}

                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                                ID: {jigsawId}
                            </Typography>
                        </Stack>
                    </Paper>

                    <Paper sx={{ p: 2 }}>
                        <Stack spacing={2}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography variant="h6" sx={{ flex: 1 }}>
                                    Color baskets
                                </Typography>
                                {colorBasketResult && (
                                    <>
                                        <Chip
                                            size="small"
                                            label={colorBasketResult.available ? "Available" : "Unavailable"}
                                            color={colorBasketResult.available ? "success" : "warning"}
                                            variant="outlined"
                                        />
                                        {colorBasketResult.basketCount != null && (
                                            <Chip size="small" label={`K=${colorBasketResult.basketCount}`} />
                                        )}
                                        {colorBasketResult.algorithmVersion && (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={colorBasketResult.algorithmVersion}
                                            />
                                        )}
                                        {colorBasketResult.requiresUnlock && (
                                            <Chip
                                                size="small"
                                                color="info"
                                                variant="outlined"
                                                label="Requires unlock"
                                            />
                                        )}
                                    </>
                                )}
                            </Box>

                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                alignItems={{ sm: "center" }}
                            >
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel id="browse-color-basket-count-label">Basket count (K)</InputLabel>
                                    <Select
                                        labelId="browse-color-basket-count-label"
                                        label="Basket count (K)"
                                        value={basketCount ?? ""}
                                        onChange={(e) => {
                                            setBasketCount(Number(e.target.value));
                                            setShowColorBaskets(false);
                                            setSelectedBasketIndex(null);
                                        }}
                                        disabled={
                                            basketBusy ||
                                            boardBasketBusy ||
                                            !jigsawId ||
                                            basketCount == null
                                        }
                                        MenuProps={{
                                            PaperProps: {
                                                sx: { maxHeight: 320 },
                                            },
                                        }}
                                    >
                                        {allowedBasketCounts.map((k) => (
                                            <MenuItem key={k} value={k}>
                                                {k}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Button
                                    variant={showColorBaskets ? "outlined" : "contained"}
                                    onClick={handleShowColorBaskets}
                                    disabled={
                                        boardBasketBusy || basketBusy || !jigsawId || basketCount == null
                                    }
                                >
                                    {boardBasketBusy
                                        ? "Loading…"
                                        : showColorBaskets
                                          ? "Hide baskets"
                                          : "Show baskets"}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleGenerateColorBaskets}
                                    disabled={
                                        basketBusy || boardBasketBusy || !jigsawId || basketCount == null
                                    }
                                >
                                    {basketBusy ? "Generating…" : "Generate (recompute)"}
                                </Button>
                                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                                    {colorBasketMeta?.available === false
                                        ? `Currently unavailable (${colorBasketMeta.reason || "unknown"})`
                                        : allowedBasketCounts.length > 0
                                          ? `Allowed K: ${allowedBasketCounts[0]}…${allowedBasketCounts[allowedBasketCounts.length - 1]} (higher K = more/smaller baskets). Defaults to lowest.`
                                          : ""}
                                </Typography>
                            </Stack>

                            {colorBasketResult && (
                                <Typography variant="body2" color="text.secondary">
                                    tileSize={colorBasketResult.tileSize ?? "—"} · pieces=
                                    {colorBasketResult.pieceCount ?? "—"}
                                    {colorBasketResult.reason ? ` · reason=${colorBasketResult.reason}` : ""}
                                    {Array.isArray(colorBasketResult.baskets)
                                        ? ` · showing ${colorBasketResult.baskets.length} baskets for K=${colorBasketResult.basketCount ?? basketCount}`
                                        : ""}
                                </Typography>
                            )}

                            {showColorBaskets && baskets.length > 0 && paths && imageUrl && grid > 0 && (
                                <AdminColorBasketTrays
                                    baskets={baskets}
                                    grid={grid}
                                    imageUrl={imageUrl}
                                    paths={paths}
                                />
                            )}

                            {colorBasketResult &&
                                (baskets.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No baskets returned.
                                    </Typography>
                                ) : (
                                    <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        spacing={3}
                                        alignItems={{ md: "flex-start" }}
                                    >
                                        <AdminColorBasketMiniMap
                                            baskets={baskets}
                                            imageUrl={imageUrl}
                                            grid={grid}
                                            highlightBasketIndex={selectedBasketIndex}
                                            emphasizeOnly={selectedBasketIndex != null}
                                            onBasketClick={handleSelectBasket}
                                        />
                                        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                Baskets — click one to practice
                                            </Typography>
                                            {baskets.map((basket) => {
                                                const hex = basket?.dominantColorHex || "#999999";
                                                const pieceIds = Array.isArray(basket?.pieceIds)
                                                    ? basket.pieceIds
                                                    : [];
                                                const isSelected =
                                                    selectedBasketIndex != null &&
                                                    Number(basket?.index) ===
                                                        Number(selectedBasketIndex);
                                                return (
                                                    <Box
                                                        key={basket?.index ?? hex}
                                                        component="button"
                                                        type="button"
                                                        onClick={() =>
                                                            handleSelectBasket(basket?.index)
                                                        }
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            textAlign: "left",
                                                            cursor: "pointer",
                                                            border: 1,
                                                            borderColor: isSelected
                                                                ? "primary.main"
                                                                : "divider",
                                                            bgcolor: isSelected
                                                                ? "action.selected"
                                                                : "transparent",
                                                            borderRadius: 1,
                                                            px: 1,
                                                            py: 0.75,
                                                            "&:hover": {
                                                                bgcolor: "action.hover",
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 18,
                                                                height: 18,
                                                                borderRadius: 0.5,
                                                                bgcolor: hex,
                                                                border: "1px solid",
                                                                borderColor: "divider",
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <Typography variant="body2">
                                                            Basket {basket?.index} ·{" "}
                                                            {pieceIds.length} pieces · {hex}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Stack>
                                ))}
                        </Stack>
                    </Paper>

                    {selectedBasket && paths && imageUrl && grid > 0 ? (
                        <Box id="admin-color-basket-practice" sx={{ width: "100%" }}>
                            <AdminColorBasketPlayPanel
                                basket={selectedBasket}
                                allBaskets={baskets}
                                basketCount={basketCount}
                                jigsawId={jigsawId}
                                grid={grid}
                                imageUrl={imageUrl}
                                paths={paths}
                                svgPathFallback={svgPathFallback}
                                onClose={() => setSelectedBasketIndex(null)}
                            />
                        </Box>
                    ) : null}
                </Stack>
            )}

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((prev) => ({ ...prev, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
